import type { InsertToPosition } from '@blocksuite/affine-shared/utils';
import type { DataViewUILogicBaseConstructor } from '../../core/view/data-view-base.js';
import {
  DataViewUIBase,
  DataViewUILogicBase,
} from '../../core/view/data-view-base.js';
import { html } from 'lit';
import { createUniComponentFromWebComponent } from '../../core/index.js';
import { createIcon } from '../../core/utils/uni-icon.js';
import type { GanttSingleView } from './gantt-view-manager.js';
import { ganttViewModel } from './define.js';
import {
  buildMonthBands,
  chartStart,
  columnAt,
  columnIndexOf,
  columnTime,
  renderScaleSwitcher,
  scaleSpec,
  snapToColumn,
  timeToX,
} from './scale.js';

const ROW_HEIGHT = 36;
const NAME_COLUMN = 240;
const VIEWPORT_HEIGHT = 520; // fallback until the scroller is measured
const OVERSCAN_ROWS = 8;
/** px of pre-rendered margin around the horizontal window */
const HORIZONTAL_MARGIN = 300;

const formatRange = (start: number, end: number) =>
  start === end
    ? new Date(start).toLocaleDateString()
    : `${new Date(start).toLocaleDateString()} — ${new Date(end).toLocaleDateString()}`;

/**
 * Gantt ("Инфокарта") UI: sticky task column + scrollable grid.
 *
 * Rows and columns are virtualised: only the window around the current scroll
 * position lives in the DOM, so a 1000-row table over a 20-year span stays
 * smooth. Bars are day-granular (floored to local day columns) — the same
 * epoch `snapToColumn` uses, so bars and grid lines always agree.
 */
class GanttViewUI extends DataViewUIBase<GanttViewUILogic> {
  private scrollY = 0;
  private scrollX = 0;
  private ganttViewportHeight = VIEWPORT_HEIGHT;
  private showAllUndated = false;
  private frame = 0;
  private anchoredToday = false;
  private resizeObserver?: ResizeObserver;

  override connectedCallback(): void {
    super.connectedCallback();
    this.dataset['testid'] = 'dv-gantt-view';
    this.resizeObserver = new ResizeObserver(() => this.requestUpdate());
    this.resizeObserver.observe(this);
  }

  override disconnectedCallback(): void {
    if (this.frame) cancelAnimationFrame(this.frame);
    this.resizeObserver?.disconnect();
    super.disconnectedCallback();
  }

  /** Scroll the "today" line into view once, right after the chart mounts. */
  override updated(): void {
    if (this.anchoredToday) return;
    const scroller = this.renderRoot.querySelector(
      '.orghub-gantt-scroll'
    ) as HTMLElement | null;
    if (!scroller) return;
    const range = this.logic.view.range$.value;
    if (range) {
      const scale = this.logic.view.scale$.value;
      const chartFrom = chartStart(range.start, scale);
      const todayX = timeToX(snapToColumn(Date.now(), 'day'), chartFrom, scale);
      scroller.scrollLeft = Math.max(
        0,
        NAME_COLUMN + todayX - scroller.clientWidth * 0.35
      );
    }
    this.anchoredToday = true;
  }

  private readonly onScroll = (event: Event) => {
    const el = event.currentTarget as HTMLElement;
    this.scrollY = el.scrollTop;
    this.scrollX = el.scrollLeft;
    this.ganttViewportHeight = el.clientHeight || VIEWPORT_HEIGHT;
    if (this.frame) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = 0;
      this.requestUpdate();
    });
  };

  private readonly onRowClick = (rowId: string) => {
    this.logic.root.openDetailPanel({
      view: this.logic.view,
      rowId,
    });
  };

  private readonly onRowKeyDown = (event: KeyboardEvent, rowId: string) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.onRowClick(rowId);
    }
  };

  override render() {
    const logic = this.logic;
    const mapping = logic.view.effectiveMapping$.value;
    const bars = logic.view.bars$.value;
    const undated = logic.view.undatedRows$.value;
    const range = logic.view.range$.value;
    const scale = logic.view.scale$.value;
    const highlightWeekends = logic.view.highlightWeekends$.value;

    if (mapping.status !== 'ready') {
      return this.renderHint(
        'В базе нет колонки с датой. Добавьте дату или диапазон дат — задачи появятся на диаграмме.'
      );
    }

    const spec = scaleSpec(scale);
    const chartFrom = range ? chartStart(range.start, scale) : 0;
    const lastIndex = range
      ? columnIndexOf(range.end + spec.step, chartFrom, scale)
      : -1;
    const totalWidth = Math.max((lastIndex + 1) * spec.width, 300);

    const firstRow = Math.max(
      0,
      Math.floor(this.scrollY / ROW_HEIGHT) - OVERSCAN_ROWS
    );
    const lastRow = Math.min(
      bars.length,
      Math.ceil((this.scrollY + this.ganttViewportHeight) / ROW_HEIGHT) +
        OVERSCAN_ROWS
    );
    const visibleBars = bars.slice(firstRow, lastRow);

    // horizontal window: only columns near the viewport, sized by the real
    // container width so wide screens never hit a blank grid
    const windowLeft = Math.max(0, this.scrollX - NAME_COLUMN);
    const viewWidth = Math.max(240, (this.clientWidth || 1200) - NAME_COLUMN);
    const firstColumn = Math.max(
      0,
      Math.floor((windowLeft - HORIZONTAL_MARGIN) / spec.width)
    );
    const lastColumn = Math.min(
      lastIndex,
      Math.ceil((windowLeft + viewWidth + HORIZONTAL_MARGIN) / spec.width)
    );
    const columns = [];
    for (let i = firstColumn; i <= lastColumn; i++) {
      columns.push(columnAt(i, chartFrom, scale));
    }

    const bands = range ? buildMonthBands(chartFrom, range.end, scale) : [];
    const headerHeight = bands.length ? 48 : 28;
    const totalHeight = bars.length * ROW_HEIGHT;

    const todayX = range
      ? Math.round(timeToX(snapToColumn(Date.now(), 'day'), chartFrom, scale))
      : -1;
    const showToday = todayX >= 0 && todayX <= totalWidth;

    return html`
      <div
        class="orghub-gantt"
        style="display:flex;flex-direction:column;gap:8px;width:100%;max-width:100%;min-width:0;position:relative"
      >
        <div
          style="display:flex;align-items:center;flex-wrap:wrap;gap:8px;font-size:12px;color:var(--affine-text-secondary-color)"
        >
          <span>Диаграмма Ганта · ${bars.length} задач с датами</span>
          ${renderScaleSwitcher(scale, next => logic.view.setScale(next))}
          ${this.renderSettings()}
          <span style="margin-left:auto"></span>
        </div>

        ${range
          ? html`
              <div
                class="orghub-gantt-scroll"
                @scroll=${this.onScroll}
                style="display:flex;align-items:flex-start;border:1px solid var(--affine-border-color);border-radius:8px;overflow:auto;max-height:max(320px, 65vh);max-width:100%;position:relative"
              >
                <div
                  style="position:sticky;left:0;z-index:2;background:var(--affine-background-primary-color);width:${NAME_COLUMN}px;min-width:${NAME_COLUMN}px;border-right:1px solid var(--affine-border-color)"
                >
                  <div
                    style="position:sticky;top:0;z-index:3;background:var(--affine-background-primary-color);height:${headerHeight}px;border-bottom:1px solid var(--affine-border-color);font-size:11px;display:flex;align-items:flex-end;padding:0 8px 6px;color:var(--affine-text-secondary-color)"
                  >
                    Задача
                  </div>
                  <div style="position:relative;height:${totalHeight}px">
                    ${visibleBars.map(
                      (bar, index) => html`
                        <div
                          style="position:absolute;top:${(firstRow + index) *
                          ROW_HEIGHT}px;left:0;right:0;height:${ROW_HEIGHT}px;display:flex;align-items:center;padding:0 8px;font-size:13px;cursor:pointer;border-bottom:1px solid var(--affine-border-color)"
                          role="button"
                          tabindex="0"
                          @click=${() => this.onRowClick(bar.rowId)}
                          @keydown=${(e: KeyboardEvent) =>
                            this.onRowKeyDown(e, bar.rowId)}
                          title=${formatRange(bar.startAt, bar.endAt)}
                        >
                          <span
                            style="overflow:hidden;text-overflow:ellipsis;white-space:nowrap"
                            >${bar.title}</span
                          >
                        </div>
                      `
                    )}
                  </div>
                </div>

                <div
                  style="position:relative;min-width:${Math.max(
                    totalWidth,
                    300
                  )}px;height:${headerHeight + totalHeight}px"
                >
                  <div
                    style="position:sticky;top:0;z-index:1;height:${headerHeight}px;background:var(--affine-background-primary-color);border-bottom:1px solid var(--affine-border-color);font-size:11px;color:var(--affine-text-secondary-color)"
                  >
                    ${bands.map(
                      band =>
                        html`<div
                          style="position:absolute;top:0;left:${band.x}px;width:${band.width}px;height:20px;display:flex;align-items:center;padding-left:6px;overflow:hidden;white-space:nowrap;font-weight:600;border-right:1px solid var(--affine-border-color)"
                        >
                          ${band.label}
                        </div>`
                    )}
                    ${columns.map(
                      column =>
                        html`<div
                          style="position:absolute;top:${bands.length
                            ? 20
                            : 0}px;left:${column.x}px;width:${column.width}px;height:28px;display:flex;align-items:center;justify-content:center;border-right:1px solid var(--affine-border-color);${column.weekend &&
                          highlightWeekends
                            ? 'background:var(--affine-hover-color);'
                            : ''}"
                        >
                          ${column.label}
                        </div>`
                    )}
                  </div>

                  <div style="position:relative;height:${totalHeight}px">
                    ${columns.map(
                      column =>
                        html`<div
                          style="position:absolute;top:0;bottom:0;left:${column.x}px;width:${column.width}px;border-right:1px solid var(--affine-border-color);${column.weekend &&
                          highlightWeekends
                            ? 'background:var(--affine-hover-color);'
                            : ''}"
                        ></div>`
                    )}
                    ${visibleBars.map((bar, index) => {
                      // day-granular bars: floor both ends to local day columns,
                      // the same epoch the grid lines use
                      const barStart = snapToColumn(bar.startAt, 'day');
                      const barEnd = columnTime(
                        1,
                        snapToColumn(bar.endAt, 'day'),
                        'day'
                      );
                      const x1 = Math.round(
                        timeToX(barStart, chartFrom, scale)
                      );
                      const x2 = Math.round(timeToX(barEnd, chartFrom, scale));
                      return html`
                        <div
                          style="position:absolute;top:${(firstRow + index) *
                          ROW_HEIGHT}px;left:0;right:0;height:${ROW_HEIGHT}px;border-bottom:1px solid var(--affine-border-color)"
                        >
                          <div
                            style="position:absolute;top:8px;height:20px;border-radius:6px;background:var(--affine-primary-color);opacity:.85;left:${x1}px;width:${Math.max(
                              x2 - x1,
                              6
                            )}px;cursor:pointer"
                            role="button"
                            tabindex="0"
                            title=${`${bar.title} · ${formatRange(
                              bar.startAt,
                              bar.endAt
                            )}`}
                            @click=${() => this.onRowClick(bar.rowId)}
                            @keydown=${(e: KeyboardEvent) =>
                              this.onRowKeyDown(e, bar.rowId)}
                          ></div>
                        </div>
                      `;
                    })}
                    ${showToday
                      ? html`<div
                          style="position:absolute;top:0;bottom:0;left:${todayX}px;width:2px;background:var(--affine-error-color);opacity:.6;pointer-events:none;z-index:4"
                          title="Сегодня"
                        ></div>`
                      : null}
                  </div>
                </div>
              </div>
            `
          : this.renderHint('В базе пока нет задач с датами.')}
        ${this.renderUndated(undated)}
      </div>
    `;
  }

  private renderSettings() {
    const view = this.logic.view;
    const mapping = view.effectiveMapping$.value;
    const selectedId =
      mapping.status === 'ready' ? mapping.propertyId : undefined;
    const dateProperties = view.dateProperties$.value;
    const highlightWeekends = view.highlightWeekends$.value;

    return html`<span style="position:relative">
      <details class="orghub-gantt-scale">
        <summary
          style="padding:3px 10px;border-radius:6px;border:1px solid var(--affine-border-color);cursor:pointer;list-style:none;display:flex;align-items:center;gap:6px;user-select:none"
        >
          <span>Настройки</span>
          <span style="opacity:.6;font-size:10px">▾</span>
        </summary>
        <div
          style="position:absolute;right:0;top:calc(100% + 4px);z-index:10;min-width:210px;padding:6px;border-radius:8px;border:1px solid var(--affine-border-color);background:var(--affine-background-primary-color);box-shadow:0 8px 24px rgba(0,0,0,.2);display:flex;flex-direction:column;gap:2px"
        >
          <div
            style="font-size:11px;color:var(--affine-text-secondary-color);padding:4px 8px 2px"
          >
            Колонка с датой
          </div>
          ${dateProperties.map(
            property =>
              html`<button
                data-date-column=${property.id}
                @click=${(event: Event) => {
                  const details = (event.currentTarget as HTMLElement).closest(
                    'details'
                  );
                  if (details) details.open = false;
                  view.setStartDateColumn(property.id);
                }}
                style="text-align:left;padding:6px 8px;border:0;border-radius:6px;background:${property.id ===
                selectedId
                  ? 'var(--affine-hover-color)'
                  : 'transparent'};color:var(--affine-text-primary-color);cursor:pointer;font-size:12px"
              >
                ${property.name$.value}
              </button>`
          )}
          <label
            style="display:flex;align-items:center;gap:8px;padding:6px 8px;font-size:12px;cursor:pointer;border-top:1px solid var(--affine-border-color);margin-top:4px"
          >
            <input
              type="checkbox"
              .checked=${highlightWeekends}
              @change=${(e: Event) =>
                view.setHighlightWeekends(
                  (e.target as HTMLInputElement).checked
                )}
            />
            Подсветка выходных
          </label>
        </div>
      </details>
    </span>`;
  }

  private renderUndated(rows: { rowId: string; title: string }[]) {
    if (!rows.length) return null;
    const LIMIT = 20;
    const visible = this.showAllUndated ? rows : rows.slice(0, LIMIT);

    return html`
      <div
        style="border:1px solid var(--affine-border-color);border-radius:8px;padding:8px 12px"
      >
        <div
          style="display:flex;align-items:center;gap:8px;font-size:12px;font-weight:600;margin-bottom:6px;color:var(--affine-text-secondary-color)"
        >
          <span>Без дат · ${rows.length}</span>
          ${rows.length > LIMIT
            ? html`<button
                @click=${() => {
                  this.showAllUndated = !this.showAllUndated;
                  this.requestUpdate();
                }}
                style="margin-left:auto;font-size:11px;font-weight:400;padding:2px 8px;border-radius:6px;border:1px solid var(--affine-border-color);background:transparent;color:inherit;cursor:pointer"
              >
                ${this.showAllUndated ? 'Свернуть' : 'Показать все'}
              </button>`
            : null}
        </div>
        <div
          style="max-height:${this.showAllUndated ? 240 : 'none'};overflow:auto"
        >
          ${visible.map(
            row =>
              html`<div
                style="font-size:13px;padding:4px 0;cursor:pointer"
                role="button"
                tabindex="0"
                @click=${() => this.onRowClick(row.rowId)}
                @keydown=${(e: KeyboardEvent) =>
                  this.onRowKeyDown(e, row.rowId)}
              >
                ${row.title}
              </div>`
          )}
        </div>
      </div>
    `;
  }

  private renderHint(text: string) {
    return html`<div
      style="padding:24px;text-align:center;font-size:13px;color:var(--affine-text-secondary-color)"
    >
      ${text}
    </div>`;
  }
}

export class GanttViewUILogic extends DataViewUILogicBase<GanttSingleView> {
  renderer = createUniComponentFromWebComponent(GanttViewUI);

  clearSelection = () => {
    this.setSelection(undefined);
  };

  addRow = (position: InsertToPosition) => {
    if (this.view.readonly$.value) return;
    const rowId = this.view.rowAdd(position);
    if (rowId) {
      this.root.openDetailPanel({
        view: this.view,
        rowId,
      });
    }
    return rowId;
  };

  focusFirstCell = () => {};

  showIndicator = () => false;

  hideIndicator = () => {};

  moveTo = () => {};
}

ganttEffects();

export const ganttViewMeta = ganttViewModel.createMeta({
  icon: createIcon('TodayIcon'),
  pcLogic: () => GanttViewUILogic as unknown as DataViewUILogicBaseConstructor,
});

export function ganttEffects() {
  if (customElements.get('affine-data-view-gantt')) {
    return;
  }
  customElements.define('affine-data-view-gantt', GanttViewUI);
}
